import { DragEvent as ReactDragEvent, useEffect, useRef, useState } from "react";
import { BsPlusLg, BsXLg, BsGripVertical } from "react-icons/bs";
import { Select } from "@/common/components/selectInput";
import Input from "@/common/components/Input/Input";
import { H3 } from "@/common/elements";
import { KeyValueObj } from "@/common/types";
import { PackagingLevelsWrapper } from "./packaging-levels-style";
import useAxiosCall from "@/hooks/useAxiosCall";
import { PACKAGING_TYPES_URL } from "@/common/constants";

const MAX_LEVELS = 5;

interface ILevel {
  uid: number;
  packagingType: string;
  // How many of the immediate child layer this layer holds (parent-owned
  // containment). Empty for the base unit (the last, smallest layer).
  quantity: string;
}

export interface IPackagingLevelsProps {
  onChange: (values: KeyValueObj, isValid: boolean) => void;
  editMode?: boolean;
  product?: KeyValueObj;
}

// Build the editable rows from a product's relational packaging_layers
// (ordered outermost -> base unit by the backend). Each layer's
// containment_quantity maps directly to that row's quantity input.
function rawLevelsFrom(
  editMode?: boolean,
  product?: KeyValueObj,
): Array<{ packagingType: string; quantity: string }> {
  if (
    editMode &&
    product &&
    Array.isArray(product.packaging_layers) &&
    product.packaging_layers.length > 0
  ) {
    return product.packaging_layers.map((layer: KeyValueObj) => ({
      packagingType: layer.packaging_type || "",
      quantity:
        layer.containment_quantity !== undefined &&
        layer.containment_quantity !== null
          ? String(layer.containment_quantity)
          : "",
    }));
  }
  return [{ packagingType: "", quantity: "" }];
}

// Each layer owns its containment_quantity (how many of the immediate child it
// holds). The base unit (last layer) holds no child, so it carries none.
function buildPackagingLayers(levels: ILevel[]): KeyValueObj[] {
  const lastIndex = levels.length - 1;
  return levels.map((lvl, i) => {
    const entry: KeyValueObj = { packaging_type: lvl.packagingType };
    if (i < lastIndex && lvl.quantity) {
      entry.containment_quantity = Number(lvl.quantity);
    }
    return entry;
  });
}

// Total base units in one outermost layer = product of every non-base layer's
// containment. Returns null until all those quantities are valid.
function computeBaseUnitTotal(levels: ILevel[]): number | null {
  if (levels.length < 2) return null;
  const nonBase = levels.slice(0, -1);
  if (!nonBase.every((lvl) => lvl.quantity && Number(lvl.quantity) > 0)) {
    return null;
  }
  return nonBase.reduce((total, lvl) => total * Number(lvl.quantity), 1);
}

export function PackagingLevels({
  onChange,
  editMode,
  product,
}: IPackagingLevelsProps) {
  const uidRef = useRef(0);
  const nextUid = () => (uidRef.current += 1);

  const withUids = (
    raw: Array<{ packagingType: string; quantity: string }>,
  ): ILevel[] => raw.map((lvl) => ({ uid: nextUid(), ...lvl }));

  const [levels, setLevels] = useState<ILevel[]>(() =>
    withUids(rawLevelsFrom(editMode, product)),
  );
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const [getPackagingTypes, packagingTypesData, , packagingTypesLoading] =
    useAxiosCall();

  const packagingTypes: KeyValueObj[] = (
    packagingTypesData?.packaging_types || []
  ).map((type: string) => ({ id: type, title: type }));

  useEffect(() => {
    getPackagingTypes({ url: PACKAGING_TYPES_URL, method: "GET" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLevels(withUids(rawLevelsFrom(editMode, product)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, product]);

  useEffect(() => {
    const lastIndex = levels.length - 1;
    const isValid =
      levels.length >= 1 &&
      levels.every((lvl, i) => {
        if (!lvl.packagingType) return false;
        // Every layer except the base unit needs a positive containment.
        if (i < lastIndex) return !!lvl.quantity && Number(lvl.quantity) > 0;
        return true;
      });
    onChange({ packaging_layers: buildPackagingLayers(levels) }, isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levels]);

  function handleTypeChange(uid: number, title: string) {
    setLevels((prev) =>
      prev.map((lvl) =>
        lvl.uid === uid ? { ...lvl, packagingType: title } : lvl,
      ),
    );
  }

  function handleQuantityChange(uid: number, value: string) {
    setLevels((prev) =>
      prev.map((lvl) => (lvl.uid === uid ? { ...lvl, quantity: value } : lvl)),
    );
  }

  function handleAddLevel() {
    setLevels((prev) =>
      prev.length >= MAX_LEVELS
        ? prev
        : [...prev, { uid: nextUid(), packagingType: "", quantity: "" }],
    );
  }

  function handleRemoveLevel(uid: number) {
    setLevels((prev) => (prev.length > 1 ? prev.filter((lvl) => lvl.uid !== uid) : prev));
  }

  function handleDragStart(e: ReactDragEvent, index: number) {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    // Use the whole row (not the small handle) as the drag image so it's clear
    // which level is being picked up.
    const row = (e.currentTarget as HTMLElement).closest(".level-row");
    if (row) e.dataTransfer.setDragImage(row as HTMLElement, 24, 24);
  }

  function handleDragOver(e: ReactDragEvent, index: number) {
    if (dragIndex === null) return;
    e.preventDefault();
    setOverIndex(index);
  }

  function endDrag() {
    setDragIndex(null);
    setOverIndex(null);
  }

  function handleDrop(targetIndex: number) {
    setLevels((prev) => {
      if (dragIndex === null || dragIndex === targetIndex) return prev;
      const reordered = [...prev];
      const [moved] = reordered.splice(dragIndex, 1);
      reordered.splice(targetIndex, 0, moved);
      return reordered;
    });
    endDrag();
  }

  function rowClassName(index: number) {
    const classes = ["level-row"];
    if (dragIndex === index) classes.push("dragging");
    if (overIndex === index && dragIndex !== null && dragIndex !== index) {
      classes.push(dragIndex > index ? "drop-above" : "drop-below");
    }
    return classes.join(" ");
  }

  function containmentPrompt(index: number) {
    const currentType = levels[index]?.packagingType || "this package";
    const childType = levels[index + 1]?.packagingType;
    // Until the child level has a type, leave its name blank rather than
    // showing a placeholder like "the next level".
    const child = childType ? `${childType}(s) ` : "";
    return `How many ${child}does one ${currentType} hold?`;
  }

  function parentLabel(index: number) {
    if (index === 0) return "Outermost (highest) level";
    const parentType = levels[index - 1]?.packagingType || "the level above";
    return `Inside one ${parentType}`;
  }

  // A packaging type may be used on only one level (enforced by a unique index
  // on the backend). Hide types already chosen on other levels, but always keep
  // this level's own current selection visible.
  function optionsForLevel(index: number): KeyValueObj[] {
    const currentType = levels[index]?.packagingType;
    const takenByOthers = new Set(
      levels
        .filter((_, i) => i !== index)
        .map((lvl) => lvl.packagingType)
        .filter(Boolean),
    );
    return packagingTypes.filter(
      (opt) => opt.title === currentType || !takenByOthers.has(opt.title),
    );
  }

  const baseUnitTotal = computeBaseUnitTotal(levels);
  const outermostType = levels[0]?.packagingType || "outermost";
  const baseType = levels[levels.length - 1]?.packagingType || "base unit";

  return (
    <PackagingLevelsWrapper>
      <div className="text-prompt">
        <H3>Product Packaging</H3>
        <p>
          Add each packaging level, starting with Level 1 as the biggest package
          and working down to the smallest. For each level, enter how many of the
          next (smaller) level it holds. Drag the handle to reorder levels.
        </p>
      </div>

      {levels.map((lvl, index) => (
        <div
          className={rowClassName(index)}
          key={lvl.uid}
          onDragOver={(e) => handleDragOver(e, index)}
          onDrop={() => handleDrop(index)}
        >
          <div className="level-head">
            <span
              className={`drag-handle${lvl.packagingType ? "" : " disabled"}`}
              aria-label={
                lvl.packagingType
                  ? "Drag to reorder"
                  : "Select a packaging type before reordering"
              }
              title={
                lvl.packagingType
                  ? "Drag to reorder"
                  : "Select a packaging type before reordering"
              }
              draggable={!!lvl.packagingType}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={endDrag}
            >
              <BsGripVertical />
            </span>
            <span className="level-parent">{parentLabel(index)}</span>
            {levels.length > 1 && (
              <button
                type="button"
                className="remove-level"
                onClick={() => handleRemoveLevel(lvl.uid)}
              >
                <BsXLg /> Remove
              </button>
            )}
          </div>

          <Select
            options={optionsForLevel(index)}
            readOnly={packagingTypesLoading}
            placeholder={
              packagingTypesLoading
                ? "Loading packaging types..."
                : `Select Level ${index + 1} packaging`
            }
            prompt={`Level ${index + 1} Packaging`}
            fullWidth={false}
            shortWidth={true}
            name={`packaging_level_${lvl.uid}`}
            indexLevel={MAX_LEVELS - index}
            formValues={{ [`packaging_level_${lvl.uid}`]: lvl.packagingType }}
            setFormValues={() => {}}
            required
            updateData={(_name, title) =>
              handleTypeChange(lvl.uid, title as string)
            }
          />
          {index < levels.length - 1 && (
            <Input
              inputType="number"
              prompt={containmentPrompt(index)}
              name={`packaging_level_${lvl.uid}_quantity`}
              placeholder="Enter quantity"
              fullWidth={false}
              shortWidth={true}
              required
              formValues={{}}
              initialValue={lvl.quantity}
              rules={{
                aboveZero: {
                  expectedValue: true,
                  errorMessage: "Value must be more than zero",
                },
              }}
              updateData={(_name: string, value: string) =>
                handleQuantityChange(lvl.uid, value)
              }
            />
          )}
        </div>
      ))}

      {baseUnitTotal !== null && (
        <p className="base-unit-total">
          One {outermostType} contains <strong>{baseUnitTotal}</strong> {baseType}
          (s) in total.
        </p>
      )}

      <div className="add-level">
        {levels.length < MAX_LEVELS ? (
          <button type="button" onClick={handleAddLevel}>
            <BsPlusLg /> Add new level
          </button>
        ) : (
          <p className="max-reached">Maximum of {MAX_LEVELS} levels reached</p>
        )}
      </div>
    </PackagingLevelsWrapper>
  );
}
